<script setup lang="ts">
import type { BreadcrumbItem } from "@nuxt/ui";

const props = withDefaults(
  defineProps<{
    breadcrumb: BreadcrumbItem[];
    type: "sites" | "structures";
    total: number;
    bottomPadding?: number;
  }>(),
  { bottomPadding: 0 }
);

const isMobile = useIsMobile();
const sheetOpen = ref(false);

const sheetRef = useTemplateRef<{ scrollEl: HTMLElement | null }>("sheetRef");
const sheetScrollEl = computed<HTMLElement | null>(() => sheetRef.value?.scrollEl ?? null);

defineExpose({ sheetScrollEl, closeSheet });

function openSheet() {
  sheetOpen.value = true;
}

function closeSheet() {
  sheetOpen.value = false;
}

const cssVars = computed(() => ({ "--bottom-padding": `${props.bottomPadding}px` }));
</script>

<template>
  <div :style="cssVars" class="flex flex-col grow -mb-(--bottom-padding)">
    <AppSection class="bg-white" :ui="{ base: 'pt-8 pb-6' }">
      <WebBreadcrumbs :items="breadcrumb" class="mb-10" />
      <slot name="search" />
    </AppSection>

    <section class="grow flex">
      <div v-if="!isMobile" class="w-90.5 shrink-0 bg-white">
        <slot name="aside" />
      </div>

      <div
        class="flex-1 relative h-svh md:h-[calc(var(--ui-content-height)+var(--bottom-padding))] bg-[#28395b]"
        :class="!isMobile ? 'sticky top-(--ui-header-height)' : ''"
      >
        <slot
          name="map"
          :is-mobile="isMobile"
          :open-sheet="openSheet"
          :trigger-label="`${$t('ui.list.show')} (${$t(`${type}.count.short`, total ?? 0)})`"
          :bottom-padding="bottomPadding"
        />
      </div>
    </section>

    <AppBottomSheet
      v-if="isMobile"
      ref="sheetRef"
      v-model:open="sheetOpen"
      class="[--header-bg:var(--color-blue-200)] [--header-border:transparent]"
    >
      <template #header>
        <span role="statuss" class="font-semibold">{{ $t(`${type}.count.zone`, total ?? 0) }}</span>
      </template>
      <slot name="aside" />
    </AppBottomSheet>
  </div>
</template>
